/**
 * Client annual budget (f10.10) -- one figure per client per calendar year.
 *
 * Grouped by Business Unit, edited inline. The currency field is PRE-FILLED
 * with the client's unit reporting currency and always sent explicitly: the
 * server never infers it. A stale save answers `409 VERSION_CONFLICT` with the
 * figure as it stands now; the row shows it and offers to re-apply.
 */

import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Pencil, RotateCw, Search, X } from 'lucide-react';

import type { BudgetCurrent, ClientAnnualBudgetItem } from '@/api/client';
import { useClientAnnualBudgets, useLookupCatalog, useSaveClientAnnualBudget } from '@/api/queries';
import { formatWhen } from '@/routes/data-import/importView';
import { EmptyCell } from '@/ui/EmptyCell';

import {
  WARNING_TEXT,
  budgetedCount,
  conflictCurrent,
  fieldError,
  groupByUnit,
  groupDigits,
  looksLikeAmount,
  normaliseAmount,
  refusalMessage,
} from './budgetView';

const CURRENCY_TYPE = 'CURRENCY';

export function useActiveCurrencies(): string[] {
  const catalog = useLookupCatalog();
  return useMemo(
    () =>
      (catalog.data?.types.find((type) => type.code === CURRENCY_TYPE)?.values ?? [])
        .filter((value) => value.is_active)
        .map((value) => value.code),
    [catalog.data],
  );
}

export function ClientAnnualPanel({ year }: { year: number }) {
  const budgets = useClientAnnualBudgets(year);
  const currencies = useActiveCurrencies();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const items = (budgets.data?.items ?? []).filter(
      (item) =>
        !needle ||
        item.client.name.toLowerCase().includes(needle) ||
        item.business_unit.name.toLowerCase().includes(needle) ||
        (item.client.sap_id ?? '').includes(needle),
    );
    return groupByUnit(items);
  }, [budgets.data, search]);

  if (budgets.isPending) return <p className="hint">Loading the {year} client budgets…</p>;
  if (budgets.isError) {
    return (
      <div className="alert alert-danger" role="alert">
        <div className="a-body">{refusalMessage(budgets.error)}</div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void budgets.refetch()}>
          <RotateCw aria-hidden="true" /> Retry
        </button>
      </div>
    );
  }

  const total = budgets.data.items.length;
  const budgeted = budgets.data.items.filter((item) => item.amount !== null).length;

  return (
    <div className="card">
      <div className="card-head">
        <h3>
          Annual budget per client <span className="hint">· {year}</span>
        </h3>
        <span className="hint">
          {budgeted} of {total} clients budgeted
        </span>
      </div>
      <div className="card-body stack-sm">
        <div className="toolbar">
          <label className="search-field">
            <Search aria-hidden="true" />
            <input
              type="search"
              placeholder="Search client, SAP ID or Business Unit"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="card-body">
          <div className="empty">
            <h3>No clients match</h3>
            <p>Clients arrive through the master import; a client with no figure this year shows an empty amount.</p>
          </div>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="data compact budget-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>SAP ID</th>
                <th className="num">Amount</th>
                <th>Currency</th>
                <th>Last updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody key={group.unitId}>
                <tr className="budget-group-row">
                  <td colSpan={6}>
                    <span className="budget-group-name">{group.unitName}</span>
                    <span className="badge badge-outline">{group.reportingCurrency}</span>
                    {!group.unitActive && <span className="badge pill-deactivated">Inactive</span>}
                    <span className="hint">
                      {budgetedCount(group)} / {group.items.length} budgeted
                    </span>
                  </td>
                </tr>
                {group.items.map((item) =>
                  editing === item.client.id ? (
                    <EditRow
                      key={item.client.id}
                      item={item}
                      year={year}
                      currencies={currencies}
                      onDone={() => setEditing(null)}
                    />
                  ) : (
                    <ViewRow key={item.client.id} item={item} onEdit={() => setEditing(item.client.id)} />
                  ),
                )}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}

function ViewRow({ item, onEdit }: { item: ClientAnnualBudgetItem; onEdit: () => void }) {
  const editable = item.client.is_active;
  return (
    <tr className={item.client.is_active ? undefined : 'is-muted'}>
      <td>
        {item.client.name}
        {!item.client.is_active && <span className="badge pill-deactivated budget-inline-badge">Inactive</span>}
        {item.warnings.map((code) => (
          <span key={code} className="budget-warning" title={WARNING_TEXT[code] ?? code}>
            <AlertTriangle aria-label={WARNING_TEXT[code] ?? code} />
          </span>
        ))}
      </td>
      <td className="mono">{item.client.sap_id ?? <EmptyCell />}</td>
      <td className="num budget-amount">{item.amount !== null ? groupDigits(item.amount) : <EmptyCell />}</td>
      <td className="mono">{item.currency_code ?? <EmptyCell />}</td>
      <td className="hint">
        {item.updated_at ? `${formatWhen(item.updated_at)}${item.updated_by ? ` · ${item.updated_by}` : ''}` : ''}
      </td>
      <td className="budget-actions">
        {editable && (
          <button type="button" className="btn btn-secondary btn-sm btn-icon" onClick={onEdit}
            title={item.amount !== null ? 'Edit this figure' : 'Set a budget'} aria-label={`Edit ${item.client.name}`}>
            <Pencil aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}

function EditRow({
  item,
  year,
  currencies,
  onDone,
}: {
  item: ClientAnnualBudgetItem;
  year: number;
  currencies: readonly string[];
  onDone: () => void;
}) {
  const save = useSaveClientAnnualBudget(year);
  const [amount, setAmount] = useState(item.amount ?? '');
  const [currency, setCurrency] = useState(item.currency_code ?? item.business_unit.reporting_currency_code);
  const [version, setVersion] = useState<number | null>(item.version ?? null);
  const conflict: BudgetCurrent | null = conflictCurrent(save.error);
  const valid = looksLikeAmount(amount);
  const amountProblem = fieldError(save.error, 'amount');
  const currencyProblem = fieldError(save.error, 'currency_code');
  const options = currencies.includes(currency) ? currencies : [currency, ...currencies];

  const submit = () => {
    if (!valid) return;
    save.mutate(
      { clientId: item.client.id, body: { amount: normaliseAmount(amount), currency_code: currency, version } },
      { onSuccess: onDone },
    );
  };

  return (
    <>
      <tr className="budget-edit-row">
        <td>{item.client.name}</td>
        <td className="mono">{item.client.sap_id ?? <EmptyCell />}</td>
        <td className="num">
          <input
            type="text"
            inputMode="decimal"
            className={`budget-input${amountProblem || (amount && !valid) ? ' has-problem' : ''}`}
            aria-label={`${item.client.name} ${year} amount`}
            value={amount}
            autoFocus
            onChange={(event) => setAmount(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
              if (event.key === 'Escape') onDone();
            }}
          />
        </td>
        <td>
          <select aria-label="Currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            {options.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </td>
        <td className="hint">
          {currency !== item.business_unit.reporting_currency_code
            ? `Unit reports in ${item.business_unit.reporting_currency_code}`
            : 'Two decimals at most'}
        </td>
        <td className="budget-actions">
          <div className="btn-row">
            <button type="button" className="btn btn-primary btn-sm btn-icon" disabled={!valid || save.isPending}
              onClick={submit} aria-label="Save">
              <Check aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-secondary btn-sm btn-icon" onClick={onDone} aria-label="Cancel">
              <X aria-hidden="true" />
            </button>
          </div>
        </td>
      </tr>
      {(conflict || amountProblem || currencyProblem || (save.error && !conflict)) && (
        <tr className="budget-feedback-row">
          <td colSpan={6}>
            {conflict ? (
              <div className="alert alert-warning" role="alert">
                <div className="a-body">
                  Someone changed this figure: it is now{' '}
                  <strong className="mono">
                    {groupDigits(conflict.amount)} {conflict.currency_code}
                  </strong>
                  {conflict.updated_by ? ` (${conflict.updated_by})` : ''}. Re-apply yours to overwrite it.
                </div>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setVersion(conflict.version);
                    save.reset();
                  }}>
                  Re-apply mine
                </button>
              </div>
            ) : (
              <div className="field-error" role="alert">
                {amountProblem ?? currencyProblem ?? refusalMessage(save.error)}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
